import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useACHEligible, useCurrentOwner, useHasAutoPay, useMainUserLoaded, useMonthlyType, useReferralEligible, useRelatedContact } from '../../hooks/useUser';
import { logout } from '../../redux/modules/authentication';
import mixpanel from '../../utilities/mixpanel';
import { MIN_DESKTOP_BREAKPOINT } from '../general/constants/_breakpoints';
import { toggleSideDrawer } from '../../redux/modules/app';
import { HEADER_HEIGHT } from '../general/constants/_sizes';
import Navigation from './Navigation';

const StyledNavigation = styled.div`
  @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
    transform: translateZ(0);

    > div > div {
      // the SideNavbar.Container from the component library in Navigation.jsx
      height: calc(100vh - ${HEADER_HEIGHT});
    }
  }
`;

const NavigationContainer = ({ drawerOpen }) => {
  const isCurrentOwner = useCurrentOwner();
  const isRelatedContact = useRelatedContact();
  const monthlyType = useMonthlyType();
  const referralEligible = useReferralEligible();
  const ACHEligible = useACHEligible();
  const hasAutoPay = useHasAutoPay();
  const isUserLoaded = useMainUserLoaded();
  const dispatch = useDispatch();
  const history = useHistory();

  const onLogout = useCallback(() => {
    mixpanel.track('Logged out');
    mixpanel.reset();

    dispatch(logout(history));
  }, [dispatch]);

  const onStorageUpdate = e => {
    if (e.key === 'AUTH_TOKEN' && !e.newValue) {
      onLogout();
    }
  };

  useEffect(() => {
    // If the auth token is removed from another tab that was logged in, log this tab out
    window.addEventListener('storage', onStorageUpdate);

    return () => {
      window.removeEventListener('storage', onStorageUpdate);
    };
  }, []);

  const drawerToggle = () => {
    dispatch(toggleSideDrawer());
  };

  return (
    <StyledNavigation data-testid="navigation">
      <Navigation
        ACHEligible={ACHEligible}
        drawerToggle={drawerToggle}
        hasAutoPay={hasAutoPay}
        isCurrentOwner={isCurrentOwner}
        isOpen={drawerOpen}
        isRelatedContact={isRelatedContact}
        loading={!isUserLoaded}
        monthlyType={monthlyType}
        onLogout={onLogout}
        referralEligible={referralEligible}
        testId="drawer"
      />
    </StyledNavigation>
  );
};

NavigationContainer.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
};

export default NavigationContainer;
